import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { IconType } from "enums/Icon";
import { isMobile } from "react-device-detect";
import { FocusOn } from "react-focus-on";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { useBlocker, useLocation } from "react-router-dom";
import { useSwipeable } from "react-swipeable";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import useOnClickOutside from "use-onclickoutside";
import Icon from "../../components/Icon/Icon";
import { hideFullscreenImage } from "../../reduxFolder/reducers/fullscreenImage";
import {
  closeRightPanel,
  openRightPanel,
} from "../../reduxFolder/reducers/rightPanel";
import {
  panelSelectData,
  panelSelectOpened,
  panelSelectType,
} from "../../reduxFolder/selectors/rightPanelSelectors";
import { breakpoints } from "../../styles/styles";
import { useTransitionEnd } from "hooks/useTransitionEnd";
import { isInIgnoredElement, PANEL_COMPONENTS } from "./helpers";
import {
  CloseButton,
  Container,
  PANEL_SLIDE_MS,
  PANEL_TRANSITION_MS,
  TransitionWrapper,
  Wrapper,
} from "./RightPanel.styles";

interface RightPanelProps {
  isOpen: boolean;
  panelType: string;
  panelData: any;
  openRightPanel: OpenRightPanel;
  closeRightPanel: () => void;
  hideFullscreenImage: () => void;
}

const RightPanel: FC<RightPanelProps> = ({
  isOpen,
  panelType,
  panelData,
  closeRightPanel,
  openRightPanel,
  hideFullscreenImage,
}) => {
  const { t } = useTranslation("common");
  const { pathname } = useLocation();
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const isScrollDisabled = isOpen && window.innerWidth <= +breakpoints.xs;

  // --- Content transition (fade-out / fade-in) on sub-view navigation ---
  const [displayedPanel, setDisplayedPanel] = useState({ panelType, panelData });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const transitionRef = useRef<HTMLDivElement | null>(null);

  // Track displayedPanel in a ref so the effect can read it without re-running
  // when setDisplayedPanel fires.
  const displayedPanelRef = useRef(displayedPanel);
  displayedPanelRef.current = displayedPanel;

  useEffect(() => {
    if (!isOpen) {
      // Closing: stop any in-flight fade so the content stays fully visible
      // while it slides off-screen. The content itself is cleared once the
      // slide actually finishes — see the close-slide useTransitionEnd below.
      setIsTransitioning(false);
      return;
    }

    const prev = displayedPanelRef.current;
    const hasContentChanged =
      panelType !== prev.panelType || panelData !== prev.panelData;

    if (!hasContentChanged) return;

    if (prev.panelType) {
      // Had previous content — fade it out. The swap-in is deferred until the
      // fade actually completes (see the content-switch useTransitionEnd below)
      // so the outgoing and incoming panels never overlap mid-transition.
      setIsTransitioning(true);
    } else {
      // Very first panel open — nothing to fade from, show immediately.
      setDisplayedPanel({ panelType, panelData });
    }
  }, [panelType, panelData, isOpen]);

  // Swap to the new content only once the fade-out has fully completed (opacity
  // is exactly 0), then fade the new content back in. Driving the swap off the
  // real transition end — rather than a timer racing the CSS — removes the brief
  // frame where the old and new content were both visible.
  useTransitionEnd(transitionRef, {
    active: isTransitioning,
    property: "opacity",
    fallbackMs: PANEL_TRANSITION_MS + 100,
    onEnd: () => {
      setDisplayedPanel({ panelType, panelData });
      setIsTransitioning(false);
    },
  });

  // Clear the panel content only after the slide-off has actually finished, so
  // it never disappears while the panel is still on screen. Event-driven, so it
  // stays correct even where the real slide duration differs from PANEL_SLIDE_MS
  // (e.g. the shorter mobile close).
  useTransitionEnd(panelRef, {
    active: !isOpen && !!displayedPanel.panelType,
    property: "transform",
    fallbackMs: PANEL_SLIDE_MS + 100,
    onEnd: () => setDisplayedPanel({ panelType: "", panelData: null }),
  });

  const DisplayedPanel =
    PANEL_COMPONENTS[displayedPanel.panelType as keyof typeof PANEL_COMPONENTS];
  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) =>
      isOpen &&
      currentLocation.pathname !== nextLocation.pathname &&
      historyAction === "POP",
  );

  const handleClose = useCallback(() => {
    if (!isOpen) return;
    closeRightPanel();
  }, [closeRightPanel, isOpen]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      handleClose();
      blocker.reset();
    }
  }, [handleClose, blocker]);

  const onClickClose = useCallback(() => {
    handleClose();
  }, [handleClose]);

  const swipeHandlers = useSwipeable({
    onSwipedRight: (eventDate) => {
      if (!isOpen) return;

      // Defer to inner swipeable regions (e.g. TabsView). The marker is read
      // here as a generic data attribute so any future nested useSwipeable can
      // opt in by spreading data-swipe-region on its container. Touch only:
      // event.target is set at touchstart and persists through touchend.
      const target = eventDate.event.target;
      if (target instanceof Element && target.closest("[data-swipe-region]")) {
        return;
      }

      handleClose();
      eventDate.event.stopPropagation();
      eventDate.event.preventDefault();
    },
    touchEventOptions: { passive: !isOpen },
  });

  // Chain react-swipeable's ref with our own panelRef (used by useOnClickOutside).
  const setRefs = useCallback(
    (el: HTMLDivElement | null) => {
      swipeHandlers.ref(el);
      (panelRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
    },
    [swipeHandlers],
  );

  // Stable reference for the mouseup listener so addEventListener/removeEventListener
  // always operate on the same function, preventing stale-listener accumulation.
  const handleCloseRef = useRef(handleClose);
  handleCloseRef.current = handleClose;

  const stableMouseUpHandler = useCallback(() => {
    handleCloseRef.current();
    window.removeEventListener("mouseup", stableMouseUpHandler, true);
  }, []);

  useOnClickOutside(panelRef, (event) => {
    if (pathname.startsWith("/parcels")) return;
    if (pathname === "/books/add-funds" && !isMobile) return;

    // HOTFIX for popover https://github.com/Andarist/use-onclickoutside/issues/1
    const target = event.target as HTMLElement | null;
    const shouldIgnore =
      isInIgnoredElement(target, "ServiceTooltipItem") ||
      isInIgnoredElement(target, "Popover") ||
      !!target?.closest?.(
        "[data-ignore-panel-close], [data-focus-lock], [data-focus-on-hidden]",
      );

    if (isOpen && !shouldIgnore) {
      window.removeEventListener("mouseup", stableMouseUpHandler, true);
      window.addEventListener("mouseup", stableMouseUpHandler, true);
    }
  });

  const handleKeyDown: EventListener = (event: Event) => {
    if (event instanceof KeyboardEvent && event.key === "Escape") {
      handleClose();
    }
  };

  useEffect(() => {
    const shouldPersistOnNavigationChange =
      panelData &&
      panelData.get("preventAutoCloseOnNavigationChange") &&
      pathname.startsWith("/parcels");

    if (shouldPersistOnNavigationChange) {
      openRightPanel(
        "STORAGE_ITEM",
        panelData.set("preventAutoCloseOnNavigationChange", false),
      );
      return;
    }

    closeRightPanel();
  }, [pathname]);

  useEffect(() => {
    if (isOpen) {
      window.addEventListener("keydown", handleKeyDown, true);
      window.addEventListener("popstate", handleClose, true);
      window.removeEventListener("mousedown", handleClose, true);
    } else {
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("popstate", handleClose, true);
    }

    return () => {
      window.removeEventListener("mouseup", stableMouseUpHandler, true);
      window.removeEventListener("mousedown", handleClose, true);
      window.removeEventListener("popstate", handleClose, true);
    };
  }, [isOpen, handleKeyDown, handleClose]);

  useEffect(() => {
    if (!isOpen) {
      hideFullscreenImage();
    }
  }, [isOpen, hideFullscreenImage]);

  return (
    <FocusOn autoFocus enabled={isScrollDisabled}>
      <Wrapper {...swipeHandlers} ref={setRefs} $isOpen={isOpen}>
        <Container>
          <CloseButton
            title={t("common.close")}
            aria-label="close-panel"
            onClick={onClickClose}
          >
            <Icon type={IconType.Close} />
          </CloseButton>
          <TransitionWrapper ref={transitionRef} $isTransitioning={isTransitioning}>
            {!!DisplayedPanel && (
              <DisplayedPanel
                isOpen={isOpen}
                key={displayedPanel.panelData}
                panelData={displayedPanel.panelData}
              />
            )}
          </TransitionWrapper>
        </Container>
      </Wrapper>
    </FocusOn>
  );
};

const withConnect = connect(
  createStructuredSelector({
    isOpen: panelSelectOpened,
    panelType: panelSelectType,
    panelData: panelSelectData,
  }),
  { closeRightPanel, openRightPanel, hideFullscreenImage },
);

export default withConnect(RightPanel);
